import React, { useEffect, useMemo, useRef, useState } from "react";
import httpClient from "../../../../api/httpClient";
import Cookies from "js-cookie";
import { Check, ClipboardList, MapPin, Package, Plus, Route, Search, Trash2, Truck } from "lucide-react";
import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import FormSection from "../../../../components/ModuleCommon/FormBuilder/FormSection";
import FieldInput from "../../../../components/ModuleCommon/FormBuilder/FieldInput";
import FieldDropdown from "../../../../components/ModuleCommon/FormBuilder/FieldDropdown";
import { useCreateShipmentDraft } from "../../../../hooks/useCreateShipmentDraft";

const emptyStop = (type, sequence) => ({
  stopNumber: String(sequence + 1),
  stopSequence: sequence,
  stopType: type,
  stopName: "",
  locationERPIdentifier: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zipCode: "",
  country: "US",
  countryISO_CODE: "US",
  locationRef: "",
  warehouseName: "",
  locationNotes: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  scheduledGateIn: "",
  scheduledGateOut: "",
});

const emptyItem = (line) => ({
  itemLineNumber: String(line),
  itemProductCode: "",
  itemDescription: "",
  quantity: "",
  containerType: "",
  handlingContainerType: "",
  handlingQuantity: "",
  totalWeight: "",
  weightUnit: "LB",
  weightQualifier: "Gross",
  totalVolume: "",
  volumeUnit: "CFT",
});

const HANDLING_CONTAINER_OPTIONS = ["", "Pallets", "Skids", "Drums", "Crates", "Totes", "Bins"];
const PRIMARY_PACKAGE_OPTIONS = [
  { value: "BG", label: "Bags (BG)" },
  { value: "BX", label: "Boxes (BX)" },
  { value: "BA", label: "Bales (BA)" },
  { value: "PC", label: "Pieces (PC)" },
  { value: "EA", label: "Each (EA)" },
];

const sectionGrid = "grid grid-cols-1 gap-fluid-3 sm:grid-cols-2 xl:grid-cols-4";
const countryOptions = ["US", "CA", "MX"];

const createSteps = [
  { id: "order",    label: "References", description: "Shipment references",  icon: ClipboardList, active: "bg-[#223688] text-white", idle: "bg-[#EDF5FF] text-[#223688]" },
  { id: "shipment", label: "Equipment",  description: "Equipment & handling", icon: Truck,         active: "bg-[#223688] text-white", idle: "bg-[#EDF5FF] text-[#223688]" },
  { id: "route",    label: "Route",      description: "Stops & schedule",     icon: Route,         active: "bg-[#223688] text-white", idle: "bg-[#EDF5FF] text-[#223688]" },
  { id: "items",    label: "Items",      description: "Freight contents",     icon: Package,       active: "bg-[#223688] text-white", idle: "bg-[#EDF5FF] text-[#223688]" },
];

const SectionTitle = ({ icon: Icon, eyebrow, title, tone }) => (
  <div className="flex items-center gap-fluid-2">
    <span className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tone}`}><Icon size={18} /></span>
    <span>
      <span className="block text-dense-xs font-bold uppercase tracking-[0.12em] text-gray-500">{eyebrow}</span>
      <span className="block text-fluid-lg font-bold text-[#030229]">{title}</span>
    </span>
  </div>
);

const ToggleField = ({ label, checked, onChange, hint }) => (
  <label className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-[#D6E4FA] bg-[#F8FBFF] px-3 py-2 text-sm font-medium text-[#223688] transition-colors hover:bg-[#EDF5FF]">
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 rounded border-[#8FA8D8] text-[#223688] focus:ring-[#223688]" />
    <span>{label}{hint ? <span className="ml-1 font-normal text-gray-500">{hint}</span> : null}</span>
  </label>
);

const LocationLookup = ({ stop, officeCode, token, onChange }) => {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const skipNextSearch = useRef(false);
  const blurTimer = useRef(null);

  useEffect(() => {
    if (skipNextSearch.current) {
      skipNextSearch.current = false;
      return undefined;
    }
    const query = stop.stopName?.trim();
    if (!query || query.length < 2) {
      setMatches([]);
      setOpen(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await httpClient.get(`/api/locations/partial-search`, {
          params: { query, limit: 8 },
          headers: { "Office-Code": officeCode },
        });
        setMatches(data || []);
        setOpen(true);
      } catch {
        setMatches([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [stop.stopName, officeCode, token]);

  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const selectLocation = (location) => {
    skipNextSearch.current = true;
    onChange({
      stopName: location.locationName || stop.stopName,
      locationERPIdentifier: location.customLocationIdentifier || "",
      addressLine1: location.addressLine1 || "",
      addressLine2: location.addressLine2 || "",
      city: location.city || "",
      state: location.state || "",
      zipCode: location.zipCode || "",
      country: location.country || "US",
      countryISO_CODE: (location.countryISO_CODE || location.country || "US").toUpperCase(),
      warehouseName: location.locationName || stop.stopName,
      locationNotes: location.notes || "",
      locationRef: location.locationRef || "",
    });
    setOpen(false);
  };

  const saveLocation = async () => {
    if (!stop.stopName?.trim()) {
      notify.error({ title: "Location name needed", message: "Enter a location name before saving it for future use." });
      return;
    }
    setSaving(true);
    try {
      const { data } = await httpClient.post(`/api/locations/add`, {
        locationName: stop.stopName,
        customLocationIdentifier: stop.locationERPIdentifier || null,
        addressLine1: stop.addressLine1 || null,
        addressLine2: stop.addressLine2 || null,
        city: stop.city || null,
        state: stop.state || null,
        zipCode: stop.zipCode || null,
        country: stop.country || "US",
        officeCode,
        locationType: "POINT",
      }, { headers: { "Office-Code": officeCode } });
      selectLocation(data);
      notify.success({ title: "Location saved", message: `${data.locationName} is now available for future shipments.` });
    } catch (error) {
      notify.error({ title: "Location could not be saved", message: error?.response?.data || error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">Location name *</label>
      <div className="relative">
        <input
          type="text"
          value={stop.stopName}
          onFocus={() => { if (matches.length && stop.stopName?.trim().length >= 2) setOpen(true); }}
          onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
          onChange={(event) => {
            skipNextSearch.current = false;
            const clearAutofill = stop.locationRef
              ? { locationERPIdentifier: "", addressLine1: "", addressLine2: "", city: "", state: "", zipCode: "", country: "US", countryISO_CODE: "US", warehouseName: "", locationNotes: "", locationRef: "" }
              : {};
            onChange({ ...clearAutofill, stopName: event.target.value });
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 pr-8 text-sm outline-none hover:border-blue-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-500 dark:border-acs-dark-border dark:bg-acs-dark-surface"
          placeholder="Search or type a location name"
        />
        <Search size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#223688]" />
      </div>
      {(open || searching) && stop.stopName?.trim().length >= 2 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-[#D6E4FA] bg-white shadow-lg dark:border-acs-dark-border dark:bg-acs-dark-surface">
          {searching && <p className="px-3 py-2 text-sm text-gray-500">Searching locations…</p>}
          {!searching && matches.map((location) => {
            const meta = [
              [location.city, location.state, location.zipCode].filter(Boolean).join(", "),
              location.customLocationIdentifier || location.locationCode || "",
            ].filter(Boolean).join(" · ");
            return (
              <button key={location.id} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => selectLocation(location)} className="block w-full border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-[#EDF5FF] dark:border-acs-dark-border dark:hover:bg-acs-dark-hover">
                <span className="block text-sm font-semibold text-[#223688]">{location.locationName}</span>
                {meta ? <span className="block text-xs text-gray-500">{meta}</span> : null}
              </button>
            );
          })}
          {!searching && !matches.length && (
            <div className="p-3">
              <p className="text-sm text-gray-500">No matching location found. You can keep this name and enter the address below, or save it for future use.</p>
              <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={saveLocation} disabled={saving} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#223688] hover:text-[#17255F] disabled:opacity-50">
                <Check size={15} /> {saving ? "Saving location…" : "Save as new location"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const INITIAL_FORM = {
  shipmentNumber: "",
  salesOrderNumbers: "",
  poNumbers: "",
  releaseNumbers: "",
  paymentMethod: "PREPAID",
  requestedMode: "TL",
  serviceType: "SHIPPING",
  orderValue: "",
  transferNumber: "",
  salesRepIdentifier: "",
  comments: "",
  portOfOrigin: "",
  portOfDestination: "",
  proNumber: "",
  workOrderNumber: "",
  bookingNumber: "",
  awbNumber: "",
  avioNumber: "",
  isHazardous: false,
  unNumber: "",
  imdgCode: "",
  flashPoint: "",
  temperatureControlled: false,
  isOversized: false,
  isFragile: false,
  isPerishable: false,
  isHighValue: false,
  isEmptyPickup: false,
  isEmptyReturn: false,
  cargoType: "LOOSE",
  isoEquipmentCode: "",
  equipmentNumber: "",
  equipmentOwnership: "",
  stops: [emptyStop("PICKUP", 0), emptyStop("DROP_OFF", 1)],
  items: [emptyItem(1)],
};

const CreateShipmentTab = ({ instanceId, setActiveTab, tabs, onCreated, onCancel }) => {
  const allOfficeCodes = (Cookies.get("officeCodes") || Cookies.get("officeCode") || "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
  const [selectedOfficeCode, setSelectedOfficeCode] = useState(allOfficeCodes[0] || "");
  const officeCode = selectedOfficeCode;
  const token = Cookies.get("authToken");
  const [loading, setLoading] = useState(false);
  const [equipmentCodes, setEquipmentCodes] = useState([]);

  const [form, setForm, clearFormDraft] = useCreateShipmentDraft(
    instanceId ? `${instanceId}-form` : null,
    INITIAL_FORM
  );
  const [activeStep, setActiveStep, clearStepDraft] = useCreateShipmentDraft(
    instanceId ? `${instanceId}-step` : null,
    "order"
  );

  const equipmentTypeKeyForMode = (mode) => {
    const m = (mode || "").toUpperCase();
    if (["TL", "FTL", "LTL"].includes(m)) return "equipment_type_tl";
    if (["INTERMODAL", "FCL", "LCL"].includes(m)) return "equipment_type_intermodal";
    return "equipment_type";
  };

  useEffect(() => {
    const freshToken = Cookies.get("authToken");
    const freshOfficeCode = selectedOfficeCode || Cookies.get("officeCode") || "";
    const codeMappingType = equipmentTypeKeyForMode(form.requestedMode);
    const loadEquipmentTypes = async () => {
      try {
        const { data } = await httpClient.get(
          `/api/code-mapping/type/${codeMappingType}`,
          { headers: { "Office-Code": freshOfficeCode } }
        );
        setEquipmentCodes(
          (data || [])
            .map((entry) => entry.description)
            .filter(Boolean)
            .sort()
        );
      } catch {
        setEquipmentCodes([]);
      }
    };
    loadEquipmentTypes();
  }, [form.requestedMode, selectedOfficeCode]);

  const equipmentTypeOptions = equipmentCodes;

  useEffect(() => {
    setForm((current) => {
      if (!current.isoEquipmentCode) return current;
      if (equipmentCodes.length === 0) return current;
      if (equipmentCodes.includes(current.isoEquipmentCode)) return current;
      return { ...current, isoEquipmentCode: "" };
    });
  }, [equipmentCodes]);

  const headerInfo = useMemo(() => ({
    "Office": allOfficeCodes.length > 1
      ? { type: "dropdown", value: selectedOfficeCode || "Select office", options: allOfficeCodes }
      : { type: "text", value: selectedOfficeCode || "No office selected", readonly: true },
    "Shipment number": { type: "text", value: form.shipmentNumber, readonly: false },
    "Payment": { type: "dropdown", value: form.paymentMethod, options: ["PREPAID", "COLLECT"] },
    "Mode": { type: "dropdown", value: form.requestedMode, options: ["TL", "FTL", "LTL", "FCL", "LCL", "AIR", "PARCEL", "INTERMODAL"] },
  }), [allOfficeCodes, selectedOfficeCode, form.shipmentNumber, form.paymentMethod, form.requestedMode]);

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const updateStop = (index, field, value) => setForm((current) => ({
    ...current,
    stops: current.stops.map((stop, stopIndex) => stopIndex === index ? { ...stop, [field]: value } : stop),
  }));
  const updateItem = (index, field, value) => setForm((current) => ({
    ...current,
    items: current.items.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
  }));

  const handleHeaderChange = (field, value) => {
    if (field === "Office") { setSelectedOfficeCode(value); return; }
    const fieldMap = { "Shipment number": "shipmentNumber", Payment: "paymentMethod", Mode: "requestedMode" };
    if (fieldMap[field]) update(fieldMap[field], value);
  };

  const returnToShipmentList = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    const index = tabs?.findIndex((tab) => tab.id === "shipment-list") ?? -1;
    if (index >= 0) setActiveTab({ index });
  };

  const buildPayload = () => {
    const salesOrderNumbers = form.salesOrderNumbers.split(",").map((value) => value.trim()).filter(Boolean);
    const poNumbers = form.poNumbers.split(",").map((value) => value.trim()).filter(Boolean);
    const releaseNumbers = form.releaseNumbers.split(",").map((value) => value.trim()).filter(Boolean);
    const items = form.items.filter((item) => item.itemProductCode || item.itemDescription).map((item) => ({
      itemLineNumber: item.itemLineNumber,
      itemProductCode: item.itemProductCode,
      itemDescription: item.itemDescription,
      itemQuantity: (() => {
        const hasP = item.containerType || item.quantity;
        const hasH = item.handlingContainerType || item.handlingQuantity;
        if (!hasP && !hasH) return [];
        const arr = [{ containerType: item.containerType || "", quantity: item.quantity || "" }];
        if (hasH) arr.push({ containerType: item.handlingContainerType || "", quantity: item.handlingQuantity || "" });
        return arr;
      })(),
      itemWeight: item.totalWeight ? { totalWeight: item.totalWeight, weightQualifier: item.weightQualifier, unitOfMeasurement: item.weightUnit } : null,
      itemVolume: item.totalVolume ? { totalVolume: item.totalVolume, unitOfMeasurement: item.volumeUnit } : null,
      salesOrderNumber: salesOrderNumbers[0] || null,
      purchaseOrderNumber: poNumbers[0] || null,
    }));
    const totalWeight = form.items.reduce((sum, item) => sum + (Number(item.totalWeight) || 0), 0);
    const totalVolume = form.items.reduce((sum, item) => sum + (Number(item.totalVolume) || 0), 0);
    const totalPallets = form.items.reduce((sum, item) => {
      const isPalletType = ["Pallets", "Pallet", "PLT", "Skids"].includes(item.handlingContainerType);
      return sum + (isPalletType ? (Number(item.handlingQuantity) || 0) : 0);
    }, 0);
    const origin = form.stops[0];
    const destination = form.stops.at(-1);

    return {
      shipmentNumber: form.shipmentNumber.trim() || null,
      officeCode,
      paymentMethod: form.paymentMethod,
      requestedMode: form.requestedMode,
      movementType: form.requestedMode,
      serviceType: form.serviceType || null,
      portOfOrigin: form.portOfOrigin.trim() || null,
      portOfDestination: form.portOfDestination.trim() || null,
      isHazardous: form.isHazardous,
      unNumber: form.isHazardous ? form.unNumber.trim() || null : null,
      imdgCode: form.isHazardous ? form.imdgCode.trim() || null : null,
      flashPoint: form.isHazardous ? form.flashPoint.trim() || null : null,
      temperatureControlled: form.temperatureControlled,
      isOversized: form.isOversized,
      isFragile: form.isFragile,
      isPerishable: form.isPerishable,
      isHighValue: form.isHighValue,
      isEmptyPickup: form.isEmptyPickup,
      isEmptyReturn: form.isEmptyReturn,
      isoEquipmentCode: form.isoEquipmentCode.trim() || null,
      equipmentNumber: form.equipmentNumber.trim() || null,
      isShipperOwned: form.equipmentOwnership ? form.equipmentOwnership === "SHIPPER" : null,
      isCarrierOwned: form.equipmentOwnership ? form.equipmentOwnership === "CARRIER" : null,
      comments: form.comments.trim() || null,
      proNumber: form.proNumber.trim() || null,
      workOrderNumber: form.workOrderNumber.trim() || null,
      bookingNumber: form.bookingNumber.trim() || null,
      awbNumber: form.awbNumber.trim() || null,
      avioNumber: form.avioNumber.trim() || null,
      salesOrderNumbers,
      poNumbers,
      releaseNumbers,
      orderValue: form.orderValue === "" ? null : Number(form.orderValue),
      createdBy: officeCode || null,
      stops: form.stops.map((stop, index) => ({
        ...stop,
        stopNumber: String(index + 1),
        stopSequence: index,
        countryISO_CODE: (stop.countryISO_CODE || stop.country || "US").toUpperCase(),
        salesOrderNumbers,
        poNumbers,
        totalWeight,
        totalWeightUnit: form.items[0]?.weightUnit || "LB",
        totalVolume,
        totalVolumeUnit: form.items[0]?.volumeUnit || "CFT",
        totalPallets,
      })),
      items,
      parties: [
        { type: "SHIPPER", name: origin.stopName, addressLine1: origin.addressLine1, addressLine2: origin.addressLine2 || null, city: origin.city, state: origin.state, zipCode: origin.zipCode, country: origin.country || "US", contactName: origin.contactName || null, contactPhone: origin.contactPhone || null, contactEmail: origin.contactEmail || null, partyOfficeCode: origin.locationERPIdentifier || origin.stopName, partyOfficeName: origin.stopName, locationRef: origin.locationRef || null, salesOrderNumbers },
        { type: "RECEIVER", name: destination.stopName, addressLine1: destination.addressLine1, addressLine2: destination.addressLine2 || null, city: destination.city, state: destination.state, zipCode: destination.zipCode, country: destination.country || "US", contactName: destination.contactName || null, contactPhone: destination.contactPhone || null, contactEmail: destination.contactEmail || null, partyOfficeCode: destination.locationERPIdentifier || destination.stopName, partyOfficeName: destination.stopName, locationRef: destination.locationRef || null, salesOrderNumbers },
      ],
    };
  };

  const validate = () => {
    if (!officeCode) return "Select an office before creating a shipment.";
    if (form.stops.length < 2) return "Add at least a pickup and a delivery stop.";
    if (form.stops.some((stop) => !stop.stopName?.trim())) {
      return "Enter a location name for every stop.";
    }
    if (form.stops.some((stop) => stop.countryISO_CODE && !/^[A-Za-z]{2}$/.test(stop.countryISO_CODE))) {
      return "Country must be a valid two-letter ISO code when provided.";
    }
    if (form.isHazardous && !form.unNumber.trim()) return "Enter the UN number for hazardous freight.";
    const pickup = form.stops[0]?.scheduledGateIn;
    const delivery = form.stops.at(-1)?.scheduledGateOut;
    if (pickup && delivery && new Date(delivery) < new Date(pickup)) return "Delivery cannot be before pickup.";
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      notify.error({ title: "Shipment cannot be created", message: error });
      return;
    }
    setLoading(true);
    try {
      const { data } = await httpClient.post(
        `/api/shipments/create`,
        buildPayload(),
        { headers: { "Content-Type": "application/json", "Office-Code": officeCode } }
      );
      notify.success({ title: "Shipment created", message: `Shipment ${data?.shipmentNumber || ""} is ready for rate shopping.` });
      clearFormDraft();
      clearStepDraft();
      onCreated?.(data);
    } catch (errorResponse) {
      notify.error({ title: "Shipment creation failed", message: errorResponse?.response?.data || errorResponse.message || "Please review the selected orders and try again." });
    } finally {
      setLoading(false);
    }
  };

  const headerButtons = [
    { label: "Cancel", type: "secondary", color: "gray", onClick: returnToShipmentList },
  ];

  const renderInput = (label, value, onChange, type = "text", required = false) => (
    <FieldInput label={required ? `${label} *` : label} type={type} value={value} onChange={onChange} />
  );

  const moveStep = (direction) => {
    const currentIndex = createSteps.findIndex((step) => step.id === activeStep);
    const nextStep = createSteps[currentIndex + direction];
    if (nextStep) setActiveStep(nextStep.id);
  };

  const StepActions = () => {
    const currentIndex = createSteps.findIndex((step) => step.id === activeStep);
    return (
      <div className="mt-fluid-4 flex items-center justify-between border-t border-gray-100 pt-fluid-3">
        <Button text="Previous" variant="outline" className="!h-11 !w-auto min-w-[9rem]" disabled={currentIndex === 0} onClick={() => moveStep(-1)} />
        {currentIndex < createSteps.length - 1 ? (
          <Button text="Continue" className="!h-11 !w-auto min-w-[9rem] !bg-[#223688] hover:!bg-[#1E2A74]" onClick={() => moveStep(1)} />
        ) : (
          <Button text="Create shipment" className="!h-11 !w-auto min-w-[11rem] !bg-[#223688] hover:!bg-[#1E2A74]" onClick={handleSubmit} disabled={loading} />
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white dark:bg-acs-dark-bg">
      <StickyHeader infoData={headerInfo} onInfoChange={handleHeaderChange} buttons={headerButtons} mode="none" />
      {loading ? <Loader message="Creating shipment…" /> : (
        <div className="flex-1 overflow-y-auto p-fluid-3">
          <div className="mx-auto max-w-[1600px] overflow-hidden rounded-xl border border-[#D6E4FA] bg-white shadow-sm dark:border-acs-dark-border dark:bg-acs-dark-surface">
            <div className="flex flex-col justify-between gap-fluid-2 border-b border-[#D6E4FA] px-fluid-4 py-fluid-4 dark:border-acs-dark-border md:flex-row md:items-end">
              <div>
                <span className="inline-flex rounded-full bg-[#223688] px-fluid-2 py-1 text-dense-xs font-bold uppercase tracking-[0.12em] text-white">New shipment</span>
                <h2 className="mt-fluid-2 text-fluid-2xl font-bold text-[#030229] dark:text-acs-dark-text">Create shipment</h2>
                <p className="mt-1 max-w-3xl text-fluid-sm text-gray-600 dark:text-gray-300">Capture shipment references, shape the route and contents, then create a shipment ready for rate shopping.</p>
              </div>
              <span className="inline-flex w-fit rounded-full bg-[#EDF5FF] px-fluid-2 py-fluid-1 text-fluid-sm font-semibold text-[#223688] dark:bg-[#223688]/30 dark:text-[#B9C7FF]">Standalone shipment</span>
            </div>

            <div className="grid grid-cols-2 border-b border-[#D6E4FA] bg-white dark:border-acs-dark-border dark:bg-acs-dark-surface-2 md:grid-cols-4">
              {createSteps.map((step, index) => {
                const Icon = step.icon;
                const selected = activeStep === step.id;
                return (
                  <button key={step.id} type="button" onClick={() => setActiveStep(step.id)} className={`group flex items-center gap-3 border-b-2 px-4 py-3 text-left transition-colors duration-200 ${selected ? "border-[#223688] bg-white text-[#223688] dark:bg-acs-dark-surface" : "border-transparent text-gray-600 hover:bg-[#EDF5FF] dark:text-gray-200 dark:hover:bg-acs-dark-hover/40"}`}>
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold ${selected ? "bg-[#223688] text-white" : step.idle}`}><Icon size={18} /></span>
                    <span className="min-w-0"><span className="block text-sm font-bold">{index + 1}. {step.label}</span><span className={`block truncate text-xs ${selected ? "text-[#5268A5]" : "text-gray-500"}`}>{step.description}</span></span>
                  </button>
                );
              })}
            </div>

            <div className="p-fluid-4">
            {activeStep === "order" && <FormSection className="border-0 bg-transparent p-0 shadow-none" title={<SectionTitle icon={ClipboardList} eyebrow="Step 1 of 4" title="Shipment references" tone="bg-[#EDF5FF] text-[#223688]" />}>
              <div className={sectionGrid}>
                {renderInput("Customer / sales-order reference(s)", form.salesOrderNumbers, (value) => update("salesOrderNumbers", value))}
                {renderInput("PO reference(s)", form.poNumbers, (value) => update("poNumbers", value))}
                {renderInput("Release number(s)", form.releaseNumbers, (value) => update("releaseNumbers", value))}
                {renderInput("Order value", form.orderValue, (value) => update("orderValue", value), "number")}
                {renderInput("PRO number", form.proNumber, (value) => update("proNumber", value))}
                {renderInput("Work order number", form.workOrderNumber, (value) => update("workOrderNumber", value))}
                {renderInput("Booking number", form.bookingNumber, (value) => update("bookingNumber", value))}
                {renderInput("AWB number", form.awbNumber, (value) => update("awbNumber", value))}
                {renderInput("Avio number", form.avioNumber, (value) => update("avioNumber", value))}
              </div>
              <p className="mt-fluid-2 text-dense-xs text-gray-500">References are optional and are stored on this shipment only. They do not create or update order records.</p>
              <StepActions />
            </FormSection>}

            {activeStep === "shipment" && <FormSection className="border-0 bg-transparent p-0 shadow-none" title={<SectionTitle icon={Truck} eyebrow="Step 2 of 4" title="Equipment details" tone="bg-[#EDF5FF] text-[#223688]" />}>
              <div className={sectionGrid}>
                <FieldDropdown label="Equipment Type" value={form.isoEquipmentCode} options={["", ...equipmentTypeOptions]} onChange={(value) => update("isoEquipmentCode", value)} />
                {renderInput("Equipment number", form.equipmentNumber, (value) => update("equipmentNumber", value))}
                <FieldDropdown label="Equipment ownership" value={form.equipmentOwnership} options={["", "SHIPPER", "CARRIER"]} onChange={(value) => update("equipmentOwnership", value)} />
              </div>
              <div className="mt-fluid-3 grid grid-cols-1 gap-fluid-2 sm:grid-cols-2 xl:grid-cols-3">
                <ToggleField label="Hazmat" checked={form.isHazardous} onChange={(value) => update("isHazardous", value)} />
                <ToggleField label="Temperature Controlled" checked={form.temperatureControlled} onChange={(value) => update("temperatureControlled", value)} />
                <ToggleField label="Overweight / oversized" checked={form.isOversized} onChange={(value) => update("isOversized", value)} />
                <ToggleField label="Fragile" checked={form.isFragile} onChange={(value) => update("isFragile", value)} />
                <ToggleField label="Perishable" checked={form.isPerishable} onChange={(value) => update("isPerishable", value)} />
                <ToggleField label="High value" checked={form.isHighValue} onChange={(value) => update("isHighValue", value)} />
              </div>
              {form.isHazardous ? <div className={`mt-fluid-3 ${sectionGrid}`}>
                {renderInput("UN number", form.unNumber, (value) => update("unNumber", value), "text", true)}
                {renderInput("IMDG code", form.imdgCode, (value) => update("imdgCode", value))}
                {renderInput("Flash point", form.flashPoint, (value) => update("flashPoint", value))}
              </div> : null}
              <StepActions />
            </FormSection>}

            {activeStep === "route" && <FormSection className="border-0 bg-transparent p-0 shadow-none" title={<SectionTitle icon={MapPin} eyebrow="Step 3 of 4" title="Route stops" tone="bg-[#EDF5FF] text-[#223688]" />}>
              {form.requestedMode === "INTERMODAL" && (
              <div className="mb-fluid-3 grid grid-cols-1 gap-fluid-2 sm:grid-cols-2">
                <ToggleField
                  label="Empty Container Pickup"
                  hint="Adds an empty-container pickup before the first stop"
                  checked={form.isEmptyPickup}
                  onChange={(value) => update("isEmptyPickup", value)}
                />
                <ToggleField
                  label="Empty Container Return"
                  hint="Adds an empty-container return after the last stop"
                  checked={form.isEmptyReturn}
                  onChange={(value) => update("isEmptyReturn", value)}
                />
              </div>
              )}
              <div className="space-y-fluid-3">
                {form.stops.map((stop, index) => (
                  <div key={`${stop.stopType}-${index}`} className="rounded-lg border border-gray-200 bg-[#FAFAFB] p-fluid-3 dark:border-acs-dark-border dark:bg-acs-dark-surface-2">
                    <div className="mb-fluid-2 flex items-center justify-between">
                      <p className="text-fluid-base font-semibold text-[#223688] dark:text-[#B9C7FF]">{stop.stopType === "PICKUP" ? "Origin" : stop.stopType === "DROP_OFF" ? "Destination" : `Via stop ${index}`}</p>
                      {index > 0 && index < form.stops.length - 1 ? <button type="button" onClick={() => setForm((current) => ({ ...current, stops: current.stops.filter((_, stopIndex) => stopIndex !== index) }))} className="text-sm font-semibold text-[#223688] hover:text-[#17255F]"><Trash2 size={15} className="mr-1 inline" />Remove</button> : null}
                    </div>
                    <div className={sectionGrid}>
                      {stop.stopType === "PICKUP" || stop.stopType === "DROP_OFF" ? (
                        <LocationLookup
                          stop={stop}
                          officeCode={officeCode}
                          token={token}
                          onChange={(values) => setForm((current) => ({ ...current, stops: current.stops.map((currentStop, stopIndex) => stopIndex === index ? { ...currentStop, ...values } : currentStop) }))}
                        />
                      ) : renderInput("Location name", stop.stopName, (value) => updateStop(index, "stopName", value), "text", true)}
                      {stop.stopType === "PICKUP" && renderInput("Port / rail terminal of origin", form.portOfOrigin, (value) => update("portOfOrigin", value))}
                      {stop.stopType === "DROP_OFF" && renderInput("Port / rail terminal of destination", form.portOfDestination, (value) => update("portOfDestination", value))}
                      {renderInput("ERP location ID", stop.locationERPIdentifier, (value) => updateStop(index, "locationERPIdentifier", value))}
                      {renderInput("Address line 1", stop.addressLine1, (value) => updateStop(index, "addressLine1", value))}
                      {renderInput("Address line 2", stop.addressLine2, (value) => updateStop(index, "addressLine2", value))}
                      {renderInput("City", stop.city, (value) => updateStop(index, "city", value))}
                      {renderInput("State / province", stop.state, (value) => updateStop(index, "state", value))}
                      {renderInput("ZIP / postal code", stop.zipCode, (value) => updateStop(index, "zipCode", value))}
                      <FieldDropdown label="Country" value={stop.countryISO_CODE} options={countryOptions} onChange={(value) => setForm((current) => ({ ...current, stops: current.stops.map((currentStop, stopIndex) => stopIndex === index ? { ...currentStop, country: value, countryISO_CODE: value } : currentStop) }))} />
                      {renderInput("Warehouse name", stop.warehouseName, (value) => updateStop(index, "warehouseName", value))}
                      {renderInput("Location notes", stop.locationNotes, (value) => updateStop(index, "locationNotes", value))}
                      {renderInput("Contact name", stop.contactName, (value) => updateStop(index, "contactName", value))}
                      {renderInput("Contact phone", stop.contactPhone, (value) => updateStop(index, "contactPhone", value))}
                      {renderInput("Contact email", stop.contactEmail, (value) => updateStop(index, "contactEmail", value), "email")}
                      {stop.stopType !== "DROP_OFF" && renderInput(stop.stopType === "PICKUP" ? "Planned pickup date" : "Scheduled arrival", stop.scheduledGateIn, (value) => updateStop(index, "scheduledGateIn", value), "date")}
                      {stop.stopType !== "PICKUP" && renderInput(stop.stopType === "DROP_OFF" ? "Planned delivery date" : "Scheduled departure", stop.scheduledGateOut, (value) => updateStop(index, "scheduledGateOut", value), "date")}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-fluid-3">
                <Button text={<><Plus size={16} /> Add via stop</>} variant="outline" onClick={() => setForm((current) => ({ ...current, stops: [...current.stops.slice(0, -1), emptyStop("VIA_POINT", current.stops.length - 1), { ...current.stops.at(-1), stopSequence: current.stops.length }] }))} />
              </div>
              <StepActions />
            </FormSection>}

            {activeStep === "items" && <FormSection className="border-0 bg-transparent p-0 shadow-none" title={<SectionTitle icon={Package} eyebrow="Step 4 of 4" title="Items" tone="bg-[#EDF5FF] text-[#223688]" />}>
              <div className="space-y-fluid-3">
                {form.items.map((item, index) => (
                  <div key={index} className="rounded-lg border border-gray-200 bg-[#FAFAFB] p-fluid-3 dark:border-acs-dark-border dark:bg-acs-dark-surface-2">
                    <div className="mb-fluid-2 flex items-center justify-between">
                      <p className="text-fluid-base font-semibold text-[#223688] dark:text-[#B9C7FF]">Item {index + 1}</p>
                      {form.items.length > 1 ? <button type="button" onClick={() => setForm((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }))} className="text-sm font-semibold text-[#223688] hover:text-[#17255F]"><Trash2 size={15} className="mr-1 inline" />Remove</button> : null}
                    </div>
                    <div className={sectionGrid}>
                      {renderInput("Line number", item.itemLineNumber, (value) => updateItem(index, "itemLineNumber", value))}
                      {renderInput("Product code", item.itemProductCode, (value) => updateItem(index, "itemProductCode", value))}
                      {renderInput("Description", item.itemDescription, (value) => updateItem(index, "itemDescription", value))}
                      <FieldDropdown label="Package Type" value={item.containerType} options={PRIMARY_PACKAGE_OPTIONS} onChange={(value) => updateItem(index, "containerType", value)} />
                      {renderInput("Package Quantity", item.quantity, (value) => updateItem(index, "quantity", value), "number")}
                      <FieldDropdown label="Handling Container Type" value={item.handlingContainerType} options={HANDLING_CONTAINER_OPTIONS} onChange={(value) => updateItem(index, "handlingContainerType", value)} />
                      {renderInput("Handling Container Quantity", item.handlingQuantity, (value) => updateItem(index, "handlingQuantity", value), "number")}
                      {renderInput("Total weight", item.totalWeight, (value) => updateItem(index, "totalWeight", value), "number")}
                      <FieldDropdown label="Weight unit" value={item.weightUnit} options={["LB", "KG"]} onChange={(value) => updateItem(index, "weightUnit", value)} />
                      <FieldDropdown label="Weight qualifier" value={item.weightQualifier} options={["Gross", "Net"]} onChange={(value) => updateItem(index, "weightQualifier", value)} />
                      {renderInput("Volume", item.totalVolume, (value) => updateItem(index, "totalVolume", value), "number")}
                      <FieldDropdown label="Volume unit" value={item.volumeUnit} options={["CFT", "CBM"]} onChange={(value) => updateItem(index, "volumeUnit", value)} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-fluid-3">
                <Button text={<><Plus size={16} /> Add item</>} variant="outline" onClick={() => setForm((current) => ({ ...current, items: [...current.items, emptyItem(current.items.length + 1)] }))} />
              </div>
              <StepActions />
            </FormSection>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateShipmentTab;
